import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../AuthProvider";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { Modal, Tab, Tabs } from "react-bootstrap";
// Components
import DisputesHeaderDark from "../Components/DisputesHeaderDark";
import DisputePageTitle from "../Components/DisputePageTitle";
import { Row, Col } from "react-bootstrap";
import { Input } from "../Components/Form/Form";
import DomainManager from "../Components/DomainManager";
import CaseFile from "../Components/CaseFile";
import CaseSummary from "../Components/CaseSummary";
import CaseReportForms from "../Components/CaseReportForms";
import { generatePDF } from "../Functions/Utilities";

// import CaseAllocation from "../Components/CaseAllocation";

const getHelp = async (setHelp, setError) => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const response = await axios.get(
      `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}help/${encodeURIComponent(
        "Manage Cases"
      )}/`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    setHelp(response.data);
  } catch (error) {
    setError("Error fetching help. Please refresh.");
    setTimeout(() => {
      setError(null);
    }, "2000");
  }
};

const getCases = (setCases, setError, setIsLoading) => {
  setIsLoading(true);
  axios
    .get(`${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}cases/`)
    .then((res) => res.data)
    .then((data) => {
      setCases(
        [...data].sort((a, b) => {
          const cleanCaseNumberA = a.casenumber.replace(/-/g, "0");
          const cleanCaseNumberB = b.casenumber.replace(/-/g, "0");

          const numA = parseInt(
            cleanCaseNumberA.slice(-5).padStart(5, "0"),
            10
          );
          const numB = parseInt(
            cleanCaseNumberB.slice(-5).padStart(5, "0"),
            10
          );

          return numB - numA;
        })
      );
    })
    .catch((err) => {
      console.error(err);
      setError("Error while fetching cases. Please refresh.");
    })
    .finally(() => setIsLoading(false));
};

const getDomains = (setDomains, setError) => {
  axios
    .get(`${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}domains/`)
    .then((res) => res.data)
    .then((data) => {
      setDomains(data);
    })
    .catch((err) => {
      console.log(err);
      setError("Error while fetching domains. Please refresh.");
    });
};

const getFirms = (setFirms, setError) => {
  axios
    .get(`${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}firms/`)
    .then((res) => res.data)
    .then((data) => {
      setFirms(
        data.sort((a, b) => {
          const textA = a.firm.toUpperCase();
          const textB = b.firm.toUpperCase();
          return textA < textB ? -1 : textA > textB ? 1 : 0;
        })
      );
    })
    .catch((err) => {
      console.log(err);
      setError("Error while fetching firms. Please refresh.");
    });
};

const handleNewCaseSubmit = async (
  values,
  setSuccess,
  setError,
  setAddingCase,
  setCases,
  setSelectedCase,
  setIsLoading
) => {
  const accessToken = localStorage.getItem("accessToken");

  try {
    const response = await axios.post(
      `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}cases/create/`,
      values,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    setCases((prevCases) => [response.data, ...prevCases]);

    setSelectedCase(null);
    setSuccess("Successfully created case");
    setAddingCase(false);
    setTimeout(() => {
      setSuccess(null);
    }, "2000");
  } catch (error) {
    setError("Error while creating new case! Please try again.");
    console.error(
      "Error creating new case:",
      error.response?.data || error.message
    );
    setTimeout(() => {
      setError(null);
    }, "2000");
  }
};

const handleSubmit = async (
  values,
  setSuccess,
  setError,
  selectedCase,
  setSelectedCase,
  setCases,
  setIsLoading
) => {
  const accessToken = localStorage.getItem("accessToken");

  try {
    const response = await axios.patch(
      `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}cases/${selectedCase.caseid}/update/`,
      values,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    setSuccess("Successfully updated case");
    setCases((prevCases) =>
      prevCases.map((c) =>
        c.caseid === response.data.caseid ? response.data : c
      )
    );

    setSelectedCase(response.data);
    setTimeout(() => {
      setSuccess(null);
    }, "2000");
  } catch (error) {
    setError("Error while updating case!");
    console.error(
      "Error creating new case:",
      error.response?.data || error.message
    );
    setTimeout(() => {
      setError(null);
    }, "2000");
  }
};

const handleCaseDelete = async (
  selectedCase,
  setCases,
  setDomains,
  setError,
  setSuccess,
  setSelectedCase,
  setIsLoading
) => {
  const accessToken = localStorage.getItem("accessToken");
  setSuccess(null);

  try {
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedCase.casenumber} from cases? This cannot be undone!`
      )
    ) {
      const response = await axios.delete(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}cases/${selectedCase.caseid}/delete/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setCases((prevCases) =>
        prevCases.filter((c) => c.caseid !== selectedCase.caseid)
      );

      setSelectedCase(null);
      setSuccess(`Deleted ${selectedCase.casenumber}!`);
      setTimeout(() => {
        setSuccess(null);
      }, "2000");
    }
  } catch (error) {
    setError("Error while deleting case");
    console.log(error.message);
    setTimeout(() => {
      setError(null);
    }, "2000");
    console.error(
      "Error deleting case:",
      error.response?.data || error.message
    );
  }
};

const getFolderIdByName = async (folderName) => {
  try {
    const response = await axios.get(
      "https://www.googleapis.com/drive/v3/files",
      {
        params: {
          q: `name='${folderName}' and mimeType='application/vnd.google-apps.folder' and '1ff5JCYUYeBHZ_2q3iM8SL6vptJUQ7NPm' in parents`,
          key: window.__RUNTIME_CONFIG__.REACT_APP_GOOGLE_DRIVE_API,
          fields: "files(id, name)",
          supportsAllDrives: true,
          includeItemsFromAllDrives: true,
        },
      }
    );

    const folders = response.data.files;
    if (folders.length > 0) {
      return folders[0].id;
    } else {
      throw new Error(`Folder '${folderName}' not found.`);
    }
  } catch (error) {
    console.error(
      "Error fetching folder ID:",
      error.response?.data || error.message
    );
    return null;
  }
};

const getFiles = async (folderName, setCaseFiles, setError) => {
  try {
    const folderId = await getFolderIdByName(folderName);
    if (!folderId) {
      return;
    }
    const response = await axios.get(
      "https://www.googleapis.com/drive/v3/files",
      {
        params: {
          q: `'${folderId}' in parents`,
          key: window.__RUNTIME_CONFIG__.REACT_APP_GOOGLE_DRIVE_API,
          fields: "files(id, name, webViewLink, mimeType)",
          supportsAllDrives: true,
          includeItemsFromAllDrives: true,
        },
      }
    );

    setCaseFiles(response.data.files);
  } catch (error) {
    console.error(
      "Error fetching files:",
      error.response?.data || error.message
    );
    setError("Error fetching files from Google Drive!");
    setTimeout(() => {
      setError(null);
    }, 2000);
  }
};

export default function ManageCases() {
  const { isLoggedIn, logout, isAdmin, loading } = useContext(AuthContext);
  const [cases, setCases] = useState(null);
  const [domains, setDomains] = useState(null);
  const [firms, setFirms] = useState(null);
  // const [allocations, setAllocations] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [addingCase, setAddingCase] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [help, setHelp] = useState(null);
  const [helping, setHelping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const [caseFiles, setCaseFiles] = useState(null);

  useEffect(() => {
    setCaseFiles([]);
    if (selectedCase) {
      getFiles(selectedCase.casenumber, setCaseFiles, setError);
    }
  }, [selectedCase]);

  useEffect(() => {
    getHelp(setHelp, setError);
    getCases(setCases, setError, setIsLoading);
    getFirms(setFirms, setError);
  }, []);

  useEffect(() => {
    getDomains(setDomains, setError);
  }, [success]);

  const uniqueYears = [
    ...new Set(
      cases?.map((item) =>
        item.casedate ? new Date(item.casedate).getFullYear() : null
      )
    ),
  ].filter((year) => year);

  return (
    <>
      <DisputesHeaderDark />
      {isAdmin && (
        <>
          <div className="relative">
            <DisputePageTitle title="Manage Cases" />
            <button
              className="absolute bottom-[-50px] right-[16px] z-50"
              onClick={() => {
                setHelping(true);
              }}
            >
              <i className="fa-solid fa-circle-question text-[#ef8d23] text-[2rem]"></i>
            </button>
          </div>
          <Modal
            show={helping}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
          >
            <Modal.Header closeButton onClick={() => setHelping(false)}>
              <Modal.Title className="text-[#ef8d23]">
                <i className="fa-solid fa-circle-question text-[#ef8d23] text-[2rem] pe-3"></i>
                {help?.model} Help
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="ql-editor">
                <div dangerouslySetInnerHTML={{ __html: help?.content }}></div>
              </div>
            </Modal.Body>
          </Modal>
          <section className="pb-[160px] overflow-hidden cover-background">
            <Row>
              <Col
                lg={3}
                className=" p-5 pt-4"
                data-test="content-manage-navbar"
              >
                <button
                  onClick={() => {
                    setAddingCase((prev) => !prev);
                    setSelectedCase(null);
                  }}
                  className="w-100 bg-[#ef8d23] text-white mb-3 py-2 hover:bg-[#ef8d23cc]"
                  data-test="content-manage-new"
                >
                  Add New Case
                </button>
                <div className="bg-lightgray p-4 text-dark">
                  {/* Year Filter Dropdown */}
                  <div className="filter-container mb-4">
                    <label htmlFor="year-filter" className="mr-2">
                      Year:
                    </label>
                    <select
                      id="year-filter"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      data-test="year-filter"
                    >
                      <option value="">All</option>
                      {uniqueYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  {isLoading && <div className="cases loading"></div>}
                  <div className="max-h-screen overflow-y-scroll">

                   {!isLoading && cases &&
                    selectedYear === "" &&
                    cases.map((caseitem) => {
                      return (
                        <p
                          key={caseitem.caseid}
                          className={`${
                            caseitem.caseid === selectedCase?.caseid
                              ? "bg-[#ef8d23] text-white"
                              : ""
                          } p-1 hover:text-[#ef8d23]`}
                        >
                          <Link
                            onClick={() => {
                              setSelectedCase(caseitem);
                              setAddingCase(false);
                              setSuccess(null);
                            }}
                            data-test="caseitem-manage-link"
                          >
                            <span className="block font-bold">
                              {caseitem.casenumber}
                            </span>
                            {caseitem.complainant} vs. {caseitem.registrant}
                          </Link>
                        </p>
                      );
                    })}
                  {cases &&
                    selectedYear !== "" &&
                    cases
                      .filter(
                        (caseitem) =>
                          new Date(caseitem.casedate).getFullYear() ===
                          parseInt(selectedYear)
                      )
                      .map((caseitem) => {
                        return (
                          <p
                            key={caseitem.caseid}
                            className={`${
                              caseitem.caseid === selectedCase?.caseid
                                ? "bg-[#ef8d23] text-white"
                                : ""
                            } p-1 hover:text-[#ef8d23]`}
                          >
                            <Link
                              onClick={() => {
                                setSelectedCase(caseitem);
                                setAddingCase(false);
                                setSuccess(null);
                              }}
                              data-test="caseitem-manage-link"
                            >
                              <span className="block font-bold">
                                {caseitem.casenumber}
                              </span>
                              {caseitem.complainant} vs. {caseitem.registrant}
                            </Link>
                          </p>
                        );
                      })}
                       </div>
                </div>
              </Col>
              <Col lg={8} className="p-5 mt-5">
                {success && (
                  <motion.div
                    initial={{
                      scale: 0.8,
                      opacity: 0,
                    }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeOut",
                    }}
                  >
                    {success && (
                      <div
                        className="my-3 p-3 rounded text-[#ef8d23]"
                        style={{ border: "2px solid #ef8d23" }}
                      >
                        {success}
                      </div>
                    )}
                  </motion.div>
                )}
                {error && (
                  <motion.div
                    initial={{
                      scale: 0.8,
                      opacity: 0,
                    }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeOut",
                    }}
                  >
                    {error && (
                      <div
                        className="my-3 p-3 rounded text-danger"
                        style={{ border: "2px solid red" }}
                      >
                        {error}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* new case form */}
                {addingCase && cases && (
                  <Formik
                    enableReinitialize={true}
                    initialValues={{
                      casenumber: `ZA${currentYear}-${String(
                        cases.length + 1
                      ).padStart(5, "0")}`,
                      complainantlc: "0",
                      complainant: "",
                      complainantemail: "",
                      casedate: "",
                      registrant: "",
                      registrantemail: "",
                      registrantlc: "0",
                      rt: "",
                    }}
                    validationSchema={Yup.object().shape({
                      casenumber: Yup.string().required("Field is required."),
                      complainantlc:
                        Yup.number().required("Field is required."),
                      registrantlc: Yup.number().required("Field is required."),
                      complainant: Yup.string().required("Field is required."),
                      complainantemail:
                        Yup.string().required("Field is required."),
                      casedate: Yup.date().required("Field is required."),
                      registrant: Yup.string().required("Field is required."),
                      registrantemail:
                        Yup.string().required("Field is required."),
                    })}
                    onSubmit={async (values, actions) => {
                      try {
                        await handleNewCaseSubmit(
                          values,
                          setSuccess,
                          setError,
                          setAddingCase,
                          setCases,
                          setSelectedCase,
                          setIsLoading
                        );
                        actions.resetForm(); // Reset form after successful submission
                      } catch (error) {
                        console.error("Error submitting form:", error);
                      }
                    }}
                  >
                    {({ isSubmitting, status }) => (
                      <Form>
                        <Input
                          labelClass="mb-[10px]"
                          label={
                            <div className="mb-[8px]">
                              Case Number
                              <span className="text-[#fb4f58]">*</span>
                            </div>
                          }
                          type="text"
                          name="casenumber"
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                        ></Input>

                        <Input
                          labelClass="mb-[10px]"
                          label={
                            <div className="mb-[8px]">
                              Case Lodgement Date
                              <span className="text-[#fb4f58]">*</span>
                            </div>
                          }
                          type="date"
                          name="casedate"
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                        ></Input>
                        <div className="mb-[8px]">
                          Complainant Legal Council
                          <span className="text-[#fb4f58]">*</span>
                        </div>
                        <Field
                          as="select"
                          name="complainantlc"
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                        >
                          {firms.map((firm) => (
                            <option value={firm.firmid}>{firm.firm}</option>
                          ))}
                        </Field>
                        <Input
                          data-test="cases-update-input"
                          showErrorMsg={true}
                          name="complainant"
                          type="text"
                          labelClass="mb-[10px]"
                          label={
                            <div className="my-[8px]">
                              Complainant
                              <span className="text-[#fb4f58]">*</span>
                            </div>
                          }
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                          placeholder="Enter the case complainant"
                        />
                        <Input
                          data-test="cases-update-input"
                          showErrorMsg={true}
                          name="complainantemail"
                          type="text"
                          labelClass="mb-[10px]"
                          label={
                            <div className="mb-[8px]">
                              Complainant Email
                              <span className="text-[#fb4f58]">*</span>
                            </div>
                          }
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                          placeholder="Enter complainant's email"
                        />
                        <div className="mb-[8px]">
                          Registrant Legal Council
                          <span className="text-[#fb4f58]">*</span>
                        </div>
                        <Field
                          as="select"
                          name="registrantlc"
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                        >
                          {firms.map((firm) => (
                            <option value={firm.firmid}>{firm.firm}</option>
                          ))}
                        </Field>
                        <Input
                          data-test="cases-update-input"
                          showErrorMsg={true}
                          name="registrant"
                          type="text"
                          labelClass="mb-[10px]"
                          label={
                            <div className="my-[8px]">
                              Registrant
                              <span className="text-[#fb4f58]">*</span>
                            </div>
                          }
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                          placeholder="Enter the case registrant"
                        />
                        <Input
                          data-test="cases-update-input"
                          showErrorMsg={true}
                          name="registrantemail"
                          type="text"
                          labelClass="mb-[10px]"
                          label={
                            <div className="mb-[8px]">
                              Registrant Email
                              <span className="text-[#fb4f58]">*</span>
                            </div>
                          }
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                          placeholder="Enter registrant's email"
                        />
                        <Input
                          data-test="cases-update-input"
                          showErrorMsg={true}
                          name="rt"
                          type="text"
                          labelClass="mb-[10px]"
                          label={
                            <div className="mb-[8px]">
                              Case Reference Number
                              <span className="text-[#fb4f58]">*</span>
                            </div>
                          }
                          className="py-[13px] px-[8px] text-md leading-[initial] w-full border-[1px] border-solid border-[#dfdfdf]"
                          placeholder="Enter reference number"
                        />
                        <button
                          className={
                            "mt-3 text-white bg-[#ef8d23] py-1 px-3 hover:bg-[#ef8d23cc]"
                          }
                          type="submit"
                          data-test="cases-manage-create"
                          onClick={() => {}}
                        >
                          Create
                        </button>
                      </Form>
                    )}
                  </Formik>
                )}

                {/* update case */}
                {selectedCase && !addingCase && (
                  <>
                    <Tabs id="uncontrolled-tab-example" className="mb-3">
                      <Tab
                        tabClassName="text-[#ef8d23] hover:text-[#ef8d23]"
                        eventKey="details"
                        title="Details"
                      >
                        {/*CASE Forms*/}

                        <CaseReportForms
                          selectedCase={selectedCase}
                          firms={firms}
                          setSuccess={setSuccess}
                          setError={setError}
                          setCases={setCases}
                          setSelectedCase={setSelectedCase}
                          handleSubmit={handleSubmit}
                          handleCaseDelete={handleCaseDelete}
                          setDomains={setDomains}
                          setIsLoading={setIsLoading}
                        />
                      </Tab>
                      <Tab
                        tabClassName="text-[#ef8d23] hover:text-[#ef8d23]"
                        eventKey="domains"
                        title="Domains"
                      >
                        {/* domains */}
                        <DomainManager
                          domains={domains.filter(
                            (domain) => domain.caseid === selectedCase.caseid
                          )}
                          selectedCase={selectedCase}
                          setSuccess={setSuccess}
                        />
                      </Tab>
                      <Tab
                        tabClassName="text-[#ef8d23] hover:text-[#ef8d23]"
                        eventKey="files"
                        title="Files"
                      >
                        {/*CASE FILES*/}
                        <CaseFile
                          selectedCase={selectedCase}
                          setError={setError}
                          caseFiles={caseFiles}
                          setCaseFiles={setCaseFiles}
                        />
                      </Tab>

                      <Tab
                        tabClassName="text-[#ef8d23] hover:text-[#ef8d23]"
                        eventKey="summary"
                        title="Summary"
                      >
                        {/*CASE SUMMARY*/}
                        <CaseSummary
                          selectedCase={selectedCase}
                          firms={firms}
                          domains={domains}
                        />
                        <button
                          className="mt-3 text-white bg-[#ef8d23] py-1 px-3 hover:bg-[#ef8d23cc]"
                          onClick={() =>
                            generatePDF("summary", selectedCase.casenumber)
                          }
                        >
                          Download Summary PDF
                        </button>
                      </Tab>
                    </Tabs>
                  </>
                )}
              </Col>
            </Row>
          </section>
        </>
      )}
      {!isLoggedIn && <DisputePageTitle title="Unauthorized" />}
      {isLoggedIn && !isAdmin && <DisputePageTitle title="Unauthorized" />}
    </>
  );
}

{
  /* <CaseAllocation allocation={allocations.find((allocation) => allocation.caseid === selectedCase.caseid)} firms={firms} selectedCase={selectedCase} setError={setError} setSuccess={setSuccess} /> */
}
